import {
  createMeetingsContext,
  MeetingContextData,
  MeetingSessionOvenMediaEmitterManager,
  MeetingSessionOvenMediaReceiverManager,
  MeetingSource,
  MeetingSourceDeviceType,
  useMeetingContextDataOnChange,
  useMeetingContextDataSelector
} from "@kalyzee/kast-web-sdk";
import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { destroyMediaStream, MediaStreamEmptyVideoTrack, MediaStreamSilentTrack, useStateWithRef } from "@kalyzee/kast-web-sdk/web-components";
import {
  OvenMediaEmitterWebRTCSessionOptions,
  OvenMediaReceiverWebRTCSessionOptions,
  RTCStreamParams
} from "@kalyzee/kast-web-sdk/webrtc";
import {
  Meeting,
  MeetingPermissions,
  MeetingRoomStatus,
  MeetingRoomType,
  MeetingSession,
  MeetingSessionAddMedia,
  MeetingSessionMedia
} from "@kalyzee/kast-web-sdk/websocket";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSdkContext } from "../contexts/sdk/context";
import { getAudioContextForVolumeAnalyzer } from "../helpers/audio";
import { toastError } from "../helpers/toast";
import { RequiredSome } from "../interfaces/utils";
import { useSettings } from "./settings";

export interface MeetingSourceControls {
  type: "aver-ptz";
  hostname?: string;
  username?: string;
  password?: string;
  [key: string]: any;
}

export type MeetingSourceExtended = RequiredSome<MeetingSource<any, { type: "camera" | "screen" }>, "extra"> & {
  controls?: MeetingSourceControls;
  visible?: boolean;
};

export const buildMediasFromSources = (
  sources: MeetingSourceExtended[],
  options?: { getEnabled?: (group: string) => { audio: boolean; video: boolean } },
): MeetingSessionAddMedia<any, any, any>[] => {
  return sources.map((s) => {
    const type = s.extra.type;
    const group = type === "camera" ? "cameras" : type === "screen" ? "screens" : "undefined";
    const enabled = options?.getEnabled?.(group) ?? { audio: true, video: true };
    return {
      video: {
        deviceType: s.devices[0].type,
        enabled: enabled.video,
      },
      audio: {
        deviceType: s.devices[0].type,
        enabled: enabled.audio,
      },
      name: s.name,
      deviceId: s.id,
      extra: {
        type,
        label: s.name,
        controls: s.controls?.type,
      },
    };
  });
};

//
const DEFAULT_MEETING_SOURCES: MeetingSourceExtended[] = [
  {
    name: "webcam",
    devices: [
      {
        mode: "audiovideo",
        type: MeetingSourceDeviceType.WEB,
        data: {
          mediaConstraints: {
            video: true,
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
          },
        },
      },
    ],
    extra: {
      type: "camera",
    },
    visible: true,
  },
];

export const useMeetingSources = (): MeetingSourceExtended[] => {
  const [settings] = useSettings();
  return useMemo(() => {
    const sources = (settings.sources ?? DEFAULT_MEETING_SOURCES).filter((s) => s.devices?.length);
    sources.forEach((s) => {
      if (!s.extra) s.extra = { type: "camera" };
      else if (!s.extra.type) s.extra.type = "camera";
    });
    return sources.filter((s) => !s.disabled) as MeetingSourceExtended[];
  }, [settings.sources]);
};

export const useMeetingVideoFeedbackSource = (): MeetingSource | undefined => {
  const [settings] = useSettings();
  return settings?.videoFeedback?.source;
};

export const useMeetingWebcamFeedbackSource = (): MeetingSourceExtended => {
  const [settings] = useSettings();
  const source = settings?.webcamFeedback?.source ?? DEFAULT_MEETING_SOURCES[0];
  return source as MeetingSourceExtended;
};

export interface MeetingContextOptions {
  ovenMediaSession?: {
    emission?: {
      options?: MeetingSessionOvenMediaEmitterManager["ovenMediaSessionOptionsExtractor"];
    };
    reception?: {
      options?: MeetingSessionOvenMediaReceiverManager["ovenMediaSessionOptionsExtractor"];
    };
  };
}

// / ! \ / ! \ / ! \ 
//
// during dev, when hot-reloading this file, the context gets recreated
// and we end up in a supposedly impossible state where we are on meetings page but with empty context
//
// Not a big deal, just needs to be known.
//
// / ! \ / ! \ / ! \ 
export const meetingsContext = createMeetingsContext();

export const useMeetingContext = () => {
  return useContext(meetingsContext);
};

export const useMeetingConfigurator = (meetingContainer: MeetingContainerAPI | MeetingContainerAPI[] | undefined, options: MeetingContextOptions) => {
  const [settings] = useSettings();
  useEffect(() => {
    if (meetingContainer === undefined) return;
    const containers = Array.isArray(meetingContainer) ? meetingContainer : [meetingContainer];
    containers.forEach((c) => {
      c.emissionManager.ovenMediaSessionOptionsExtractor = (mediaSession, mediaStream) => {
        const result: OvenMediaEmitterWebRTCSessionOptions = {
          waitingForSessionReadyTimeout: 10000,
        };
        const curr = options?.ovenMediaSession?.emission?.options?.(mediaSession, mediaStream);
        if (curr) Object.assign(result, curr);
        return result;
      };
      c.receptionManager.ovenMediaSessionOptionsExtractor = (session: MeetingSession, mediaSession: MeetingSessionMedia) => {
        const result: OvenMediaReceiverWebRTCSessionOptions = {};
        const curr = options?.ovenMediaSession?.reception?.options?.(session, mediaSession);
        if (curr) Object.assign(result, curr);
        return result;
      };
    });
  }, [settings, meetingContainer, options]);
};

export const useMeetingDataOnChange = <T>(
  state: MeetingContainerAPI | undefined,
  selector: (state: MeetingContextData) => T,
  callback: (value: T, previous: T) => void
) => {
  useMeetingContextDataOnChange(state, selector, callback);
};

interface StreamData {
  stream: MediaStream;
  ready: boolean;
}

export const useDynamicMedia = (
  meetingContainer: MeetingContainerAPI | undefined,
  callbacks?: {
    onStreamStart?: (name: string, success: boolean) => void;
  }
) => {
  const session = useMeetingContextDataSelector(meetingContainer, (state) => state.session);

  const streamDataRef = useRef<Record<string, StreamData>>({});
  const [, setForceUpdate] = useState({});

  // there was issues when storing streams in a state, the "set" function is being called too quickly
  // problem solved with a ref but we still need to force renders for the get() function
  const update = () => setForceUpdate({});

  const startStreamOnMedia = async (name: string, media: MeetingSessionMedia, streamData: StreamData, params?: RTCStreamParams): Promise<boolean> => {
    if (!meetingContainer) return false;
    streamData.ready = false;
    const success = await meetingContainer.setMediaStream(media.id, streamData.stream, params);
    streamData.ready = true;
    if (!success) return false;
    callbacks?.onStreamStart?.(name, !!success);
    return true;
  };

  useEffect(() => {
    if (!meetingContainer) return;
    const ovenMediaSessionListener = meetingContainer.emissionManager.addEventListener("create", (s) => {
      const mediaName = s.data.mediaSession.name;
      if (!mediaName) return;

      const streamData = streamDataRef.current[mediaName];
      if (!streamData) return;

      startStreamOnMedia(mediaName, s.data.mediaSession, streamData);
    });
    return () => {
      ovenMediaSessionListener?.flush();
    };
  }, [meetingContainer, callbacks]);

  const set = async (
    name: string,
    stream: MediaStream,
    additionalData?: { extraType?: string; deviceId?: string },
    params?: RTCStreamParams
  ): Promise<boolean> => {
    if (!meetingContainer) return false;
    if (!session) return false;

    const currentStreamData = streamDataRef.current[name];

    if (currentStreamData) {
      if (!currentStreamData.ready) return false;
      const currentMedia = session.medias.find((m) => m.name === name);
      if (currentMedia) {
        // stream has been set and a media exists remotely
        currentStreamData.stream = stream;
        update();
        return startStreamOnMedia(name, currentMedia, currentStreamData, params);
      }
      return false; // a stream with this name is already waiting for a media
    }

    streamDataRef.current[name] = { stream, ready: false };
    update();

    const { deviceId, extraType } = additionalData ?? {};
    const group = extraType === "camera" ? "cameras" : extraType === "screen" ? "screens" : "undefined";
    const result = await meetingContainer.updater.setMedias(session.id, [
      ...session.medias,
      {
        video: {
          deviceType: "camera",
          enabled: meetingContainer.emissionManager.videoIsEnabled({ group, mode: "some" }),
        },
        audio: {
          deviceType: "audio",
          enabled: meetingContainer.emissionManager.audioIsEnabled({ group, mode: "some" }),
        },
        name: name,
        deviceId,
        extra: extraType
          ? {
              type: extraType,
            }
          : undefined,
      },
    ]);
    if (result?.error || !result?.response) {
      console.error(`Could not set media "${name}" : `, result?.error);
      remove(name);
      return false;
    }

    streamDataRef.current[name].ready = true;

    return true;
  };

  // TODO : add a way to update the stream
  const get = (name: string) => streamDataRef.current[name]?.stream;

  const remove = async (name: string): Promise<boolean> => {
    if (!meetingContainer) return false;
    if (!session) return false;
    if (!streamDataRef.current[name]) return false;

    delete streamDataRef.current[name];
    update();

    // TODO : what if this fails, we need to mark it as needing to be removed
    const result = await meetingContainer.updater.setMedias(
      session.id,
      session.medias.filter((m) => m.name !== name)
    );
    if (result?.error || !result?.response) {
      console.error(`Could not remove media "${name}" : `, result?.error);
      return false;
    }

    return true;
  };

  return {
    set,
    get,
    remove,
  };
};

export const useHasOngoingGroups = (context: MeetingContainerAPI | undefined): boolean => {
  const rooms = useMeetingContextDataSelector(context, (state) => state.rooms);
  return useMemo(() => {
    const match = rooms?.some((r) => r.type === MeetingRoomType.Group && r.status === MeetingRoomStatus.Ongoing);
    return !!match;
  }, [rooms]);
};

export const RestrictedAudioPermission = "[[role;>=;moderator]||[highlighting;==;ongoing]]";

export const usePermission = (meeting: Meeting | undefined) => {
  const sdkCtx = useSdkContext();
  return useMemo(() => {
    const hasPermission = (category: keyof MeetingPermissions, permission: string) => {
      return meeting?.permissions?.[category]?.includes(permission);
    };

    const setPermission = async (category: keyof MeetingPermissions, permission: string, enabled: boolean) => {
      const meetingId = meeting?.id;
      if (!meetingId) return;

      const newPermissions = { ...(meeting.permissions ?? {}) };
      const categoryPermissions = new Set<string>(meeting.permissions?.[category] ?? []);

      if (enabled) categoryPermissions.add(permission);
      else categoryPermissions.delete(permission);
      newPermissions[category] = Array.from(categoryPermissions);

      const websocket = sdkCtx.client.getWebsocketClient();
      const result = await websocket.emitter.meeting.updateById({ id: meetingId, permissions: newPermissions });
      if (!result.response || result.error) {
        toastError("Une erreur est survenue...");
        return;
      }
    };

    const addPermission = async (category: keyof MeetingPermissions, permission: string) => setPermission(category, permission, true);

    const removePermission = async (category: keyof MeetingPermissions, permission: string) => setPermission(category, permission, false);

    return { hasPermission, setPermission, addPermission, removePermission };
  }, [meeting])
};

/**
 * - the webcam management API is handled on the expert meeting level, wrapping all
 * the possible expert pages
 * - if we pass from one view requiring the expert webcam to an other one requiring it too,
 * we now longer have to delete and set the expert_webcam, which leads to errors
 */

export const useDynamicWebcam = (meetingContainerAPI: MeetingContainerAPI | undefined, name: string, stream?: MediaStream) => {
  const dynamicMediaAPI = useDynamicMedia(meetingContainerAPI);

  useEffect(() => {
    if (stream) {
      dynamicMediaAPI.set(name, stream, { extraType: "camera" });
    } else {
      dynamicMediaAPI.remove(name);
    }
  }, [dynamicMediaAPI, stream]);

  useEffect(() => {
    return () => {
      dynamicMediaAPI.remove(name);
    };
  }, []);
};

export const useEmptyMediaStream = (enabled: boolean = false): MediaStream | undefined => {
  const videoTrackRef = useRef<MediaStreamEmptyVideoTrack>();
  const audioTrackRef = useRef<MediaStreamSilentTrack>();
  const [mediaStream, setMediaStream, mediaStreamRef] = useStateWithRef<MediaStream>();

  const generateVideoTrack = () => {
    let result: MediaStreamEmptyVideoTrack | undefined = undefined;
    try {
      result = new MediaStreamEmptyVideoTrack();
    } catch (err) {}
    return result;
  };

  const generateAudioTrack = () => {
    let result: MediaStreamSilentTrack | undefined = undefined;
    try {
      result = new MediaStreamSilentTrack(getAudioContextForVolumeAnalyzer());
    } catch (err) {}
    return result;
  };

  const generateMediaStream = () => {
    if (!videoTrackRef.current) videoTrackRef.current = generateVideoTrack();
    if (!audioTrackRef.current) audioTrackRef.current = generateAudioTrack();

    const videoTrack = videoTrackRef.current?.track;
    const audioTrack = audioTrackRef.current?.track;
    if (!videoTrack || !audioTrack) return undefined;
    return new MediaStream([videoTrack, audioTrack]);
  };

  const clean = () => {
    videoTrackRef.current?.destroy();
    audioTrackRef.current?.destroy();
    if (mediaStreamRef.current) destroyMediaStream(mediaStreamRef.current);
  };

  useEffect(() => {
    if (enabled) {
      setMediaStream(generateMediaStream())
    }
    return () => clean();
  }, [enabled]);

  return mediaStream;
};
